import { FormEvent } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ShieldCheck } from "lucide-react";
import { api, formatApiError } from "../api/client";

const brandLogo = `${import.meta.env.BASE_URL}brand/aviza-logo.png`;

type ResetInfo = {
  email: string;
  name: string;
  expiresAt: string;
  organization: { name: string };
};

export function PasswordResetPage({ token, onReset }: { token: string; onReset: () => void }) {
  const reset = useQuery({
    queryKey: ["password-reset", token],
    queryFn: () => api<ResetInfo>(`/api/admin/auth/password-reset/${token}`)
  });
  const submitReset = useMutation({
    mutationFn: (body: unknown) => api("/api/admin/auth/password-reset/" + token, {
      method: "POST",
      body: JSON.stringify(body)
    }),
    onSuccess: () => {
      window.history.replaceState(null, "", adminHomePath());
      onReset();
    }
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const password = String(form.get("password") || "");
    const confirmPassword = String(form.get("confirmPassword") || "");
    if (password !== confirmPassword) {
      submitReset.reset();
      return;
    }
    submitReset.mutate({ password });
  }

  return (
    <main className="login-shell">
      <section className="login-panel">
        <div className="brand login-brand">
          <img className="brand-logo" src={brandLogo} alt="" aria-hidden="true" />
          <span>Aviza</span>
        </div>
        <h1 className="login-title">Reset lozinke</h1>
        {reset.isLoading && <p>Provjera reset linka...</p>}
        {reset.isError && <p className="form-error">Reset link nije aktivan ili je istekao.</p>}
        {reset.data && (
          <form className="compact-form" onSubmit={submit}>
            <p>{reset.data.organization.name} / {reset.data.email}</p>
            <label>
              Nova lozinka
              <input name="password" type="password" minLength={12} required autoComplete="new-password" />
            </label>
            <label>
              Ponovi lozinku
              <input name="confirmPassword" type="password" minLength={12} required autoComplete="new-password" />
            </label>
            <p className="form-note">Link vazi do {new Date(reset.data.expiresAt).toLocaleString()}.</p>
            {submitReset.error && <p className="form-error">{formatApiError(submitReset.error)}</p>}
            <button type="submit" disabled={submitReset.isPending}><ShieldCheck size={16} /> Postavi lozinku</button>
          </form>
        )}
      </section>
    </main>
  );
}

function adminHomePath() {
  return window.location.pathname.startsWith("/admin") ? "/admin/" : "/";
}

export function RequiredPasswordChangePage({ onChanged }: { onChanged: () => void }) {
  const change = useMutation({
    mutationFn: (body: unknown) => api("/api/admin/auth/change-password", {
      method: "POST",
      body: JSON.stringify(body)
    }),
    onSuccess: onChanged
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const password = String(form.get("password") || "");
    const confirmPassword = String(form.get("confirmPassword") || "");
    if (password !== confirmPassword) return;
    change.mutate({ password });
  }

  return (
    <main className="login-shell">
      <form className="login-panel" onSubmit={submit}>
        <div className="brand login-brand">
          <img className="brand-logo" src={brandLogo} alt="" aria-hidden="true" />
          <span>Aviza</span>
        </div>
        <h1 className="login-title">Promjena lozinke</h1>
        <p className="form-note">Prije ulaska u admin panel morate postaviti novu lozinku.</p>
        <label>
          Nova lozinka
          <input name="password" type="password" minLength={12} autoComplete="new-password" required />
        </label>
        <label>
          Ponovi lozinku
          <input name="confirmPassword" type="password" minLength={12} autoComplete="new-password" required />
        </label>
        {change.error && <p className="form-error">{formatApiError(change.error)}</p>}
        <button type="submit" disabled={change.isPending}>Sacuvaj lozinku</button>
      </form>
    </main>
  );
}
